import { useState, useEffect } from 'react';
import { Button, IconButton, Tooltip, Card, Avatar, Chip, Skeleton } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import DOMPurify from 'dompurify';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import EnterContactNumberForm from '@LandingForms/EnterContactNumberForm';
import VerifyUpdateEmailForm from '@LandingForms/VerifyUpdateEmailForm';
import ErrorHandler from '@Root/ErrorHandler';
import { getCountries, getStates } from 'country-state-picker';
import {
    IoPersonOutline,
    IoMailOutline,
    IoCallOutline,
    IoLocationOutline,
    IoBriefcaseOutline,
    IoInformationCircleOutline,
    IoCamera,
    IoTrash,
    IoCheckmarkCircle,
    IoPencil,
    IoGlobeOutline,
    IoBusinessOutline
} from 'react-icons/io5';
import { FaRegCircleUser as ProfilePicture } from "react-icons/fa6";
import { useReduxUsers } from '@Hooks/reduxUsersHook/useReduxUsers';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './ProfilePage.scss';

export default function ProfilePage(props) {
    const [cursor, setCursor] = useState('auto');
    const [user, setUser] = useState();
    const [email, setEmail] = useState('');
    const [photo, setPhoto] = useState('');
    const [fullName, setFullName] = useState('');
    const [bio, setBio] = useState('');
    const [company, setCompany] = useState('');
    const [designation, setDesignation] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadStatus, setUploadStatus] = useState('');
    const [edited, setEdited] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidImage, setIsValidImage] = useState(true);
    const [states, setStates] = useState([]);
    const [country, setCountry] = useState('');
    const [userState, setUserState] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [userCountry, setUserCountry] = useState({});
    const [showEnterContactNumberForm, setShowEnterContactNumberForm] = useState(false);
    const [openVerifyEmailForm, setOpenVerifyEmailForm] = useState(false);
    const [loading, setLoading] = useState(true);

    const { setUpdateUser } = useReduxUsers();
    const { setActiveUser, activeUser, activeUserId } = useReduxActiveUser();
    const userInfoService = new UserInfoService();
    const orgService = new OrgService();
    const errorHandler = new ErrorHandler();
    let countries = getCountries();
    const APC_ENABLE_INTEGRATIONS_WHATSAPP = JSON.parse(localStorage.getItem('APC_ENABLE_INTEGRATIONS_WHATSAPP'));
    const className = 'profilePage';

    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060'];

    useEffect(() => {
        renderData();
    }, []);

    useEffect(() => {
        if (countryCode) {
            setStates(getStates(countryCode));
        }
    }, [countryCode]);

    const renderData = async () => {
        try {
            setLoading(true);
            let userBody = activeUser;
            setCountry(userBody.country === undefined || userBody.country === null ? '' : userBody.country);
            setCountryCode(userBody.countryCode === undefined || userBody.countryCode === null ? '' : userBody.countryCode);
            setUserState(userBody.state === undefined || userBody.state === null ? '' : userBody.state);
            setUserCountry({
                'name': userBody.country === undefined || userBody.country === null ? '' : userBody.country,
                'code': userBody.countryCode === undefined || userBody.countryCode === null ? '' : userBody.countryCode
            });
            setUser(userBody);
            setFullName(userBody.fullName);
            setEmail(userBody.email);
            setDesignation(userBody.designation);
            setImageUrl(
                !!userBody.photo
                ? URL.createObjectURL(dataURLtoFile(userBody.photo))
                : ''
            );
            setEdited(false);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleEnterContactNumberForm = () => {
        setShowEnterContactNumberForm(!showEnterContactNumberForm);
    };

    const timeout = (delay) => {
        return new Promise(res => setTimeout(res, delay));
    };

    const handleImageChange = () => {
        document.getElementById('fileInput').click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file !== undefined && file !== null) {
            if (validateImage(file)) {
                const base64File = await convertFileToBase64(file);
                setPhoto(base64File);
                setImageUrl(URL.createObjectURL(file));
                setIsValidImage(true);
                setCursor('wait');
                await timeout(300);
                let userObj = await userInfoService.updateProfilePicture(activeUser.id, base64File);
                setUpdateUser(activeUser.id, { body: userObj.body });
                userObj = JSON.parse(userObj.body);
                setUser(userObj);
                setActiveUser(userObj);
                SnackbarManager.success('Profile image updated');
                setCursor('auto');
            } else {
                setIsValidImage(false);
            }
        }
    };

    const handleFullNameChange = (e) => {
        let fullNameObj = DOMPurify.sanitize(e.target.value);
        setFullName(fullNameObj);
        setEdited(true);
    };

    const handleDesignationChange = (e) => {
        let sanitizeDesignation = DOMPurify.sanitize(e.target.value);
        setDesignation(sanitizeDesignation);
        setEdited(true);
    };

    const handleAboutChange = (e) => {
        const value = DOMPurify.sanitize(e.target.value);
        setUser(prev => ({ ...(prev || {}), about: value }));
        setEdited(true);
    };

    const handleCompanyChange = (e) => {
        const value = DOMPurify.sanitize(e.target.value);
        setUser(prev => ({ ...(prev || {}), company: value }));
        setEdited(true);
    };

    const handleCountryChange = (e, value) => {
        setEdited(true);
        if (!value) {
            setCountry('');
            setCountryCode('');
            setUserCountry(null);
            setStates([]);
            setUserState('');
            return;
        }
        setStates(getStates(value.code));
        setCountry(value.name);
        setCountryCode(value.code);
        setUserCountry(value);
        setUserState('');
    };

    const handleStateChange = (e, value) => {
        setEdited(true);
        setUserState(value || '');
    };

    const handleSubmit = async () => {
        setCursor('wait');
        try {
            if (validateEmail(user.email)) {
                let userObj = user;
                userObj.country = country;
                userObj.countryCode = countryCode;
                userObj.state = userState;
                userObj.fullName = fullName;
                userObj.designation = designation;
                setEdited(false);
                let updatedUser = await userInfoService.updateUserInfo(userObj);
                if (updatedUser.body == undefined) {
                    SnackbarManager.error('Failed to update. Please try again!');
                    return;
                }
                setUpdateUser(activeUser.id, { body: updatedUser.body });
                updatedUser = JSON.parse(updatedUser.body);
                setUser(updatedUser);
                setActiveUser(updatedUser);
                SnackbarManager.success('Successfully updated profile');
            } else {
                setIsValidEmail(false);
                SnackbarManager.error('Please enter a valid email');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const convertFileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const dataURLtoFile = (dataurl) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        const ext = mime.split('/')[1] || 'jpg';
        return new File([u8arr], `photo.${ext}`, { type: mime });
    };

    const validateEmail = (value) => {
        const validator = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;
        return validator.test(value || '');
    };

    const validateImage = (file) => {
        if (file.type.split('/')[0] === 'image') {
            let sizeLimit = 1024 * 1024;
            if (file.size > sizeLimit) {
                setUploadStatus('Image size exceeded 1MB!');
                SnackbarManager.error('Image size exceeded 1MB!');
                return false;
            }
            return true;
        } else {
            setUploadStatus('Upload image file!');
            return false;
        }
    };

    const handleRemoveProfilePicture = async () => {
        setCursor('wait');
        try {
            if (user.photo == undefined || user.photo === '') return;
            let response = await userInfoService.removeProfilePicture(user.id);
            if (response.status < 400) {
                setUpdateUser(activeUser.id, { body: response.body });
                let updatedUser = JSON.parse(response.body);
                setUser(updatedUser);
                setActiveUser(updatedUser);
                setImageUrl('');
                SnackbarManager.success(response.message || 'Profile image removed');
            } else {
                SnackbarManager.error(response.message || 'Unable to remove image');
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const toggleVerifyEmailUpdateForm = () => {
        setOpenVerifyEmailForm(!openVerifyEmailForm);
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    if (loading) {
        return (
            <div className={className}>
                <div className={className + '__container'}>
                    <Card className={className + '__header'}>
                        <Skeleton variant="circular" width={120} height={120} />
                        <div style={{ flex: 1 }}>
                            <Skeleton width="40%" height={32} />
                            <Skeleton width="30%" height={24} sx={{ mt: 1 }} />
                            <Skeleton width="50%" height={20} sx={{ mt: 1 }} />
                        </div>
                    </Card>
                    {[...Array(3)].map((_, i) => (
                        <Card key={i} sx={{ p: 3, mt: 3 }}>
                            <Skeleton width="30%" height={24} sx={{ mb: 2 }} />
                            <Skeleton width="100%" height={60} />
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className={className} style={{ cursor }}>
            <div className={className + '__container'}>

                {/* Profile Header */}
                <Card className={className + '__header'}>
                    <div className={className + '__header__avatar'}>
                        <input
                            type='file'
                            accept="image/jpeg, image/png, image/webp"
                            id='fileInput'
                            onChange={handleFileChange}
                        />
                        {imageUrl ? (
                            <img src={imageUrl} alt="profile" className={className + '__header__avatar__img'} />
                        ) : (
                            <Avatar
                                sx={{
                                    width: 120,
                                    height: 120,
                                    fontSize: '2.5rem',
                                    fontWeight: 700,
                                    bgcolor: colorList[fullName?.charCodeAt(0) % colorList.length]
                                }}
                            >
                                {getInitials(fullName)}
                            </Avatar>
                        )}
                        <div className={className + '__header__avatar__overlay'}>
                            <IconButton
                                size="small"
                                className={className + '__header__avatar__btn'}
                                onClick={handleImageChange}
                            >
                                <IoCamera />
                            </IconButton>
                            {!!imageUrl && (
                                <IconButton
                                    size="small"
                                    className={className + '__header__avatar__btn ' + className + '__header__avatar__btn--delete'}
                                    onClick={handleRemoveProfilePicture}
                                >
                                    <IoTrash />
                                </IconButton>
                            )}
                        </div>
                    </div>

                    <div className={className + '__header__info'}>
                        <div className={className + '__header__primary'}>
                            <h1 className={className + '__header__name'}>{fullName || 'User Name'}</h1>
                            <Chip
                                label={`@${user?.userName}`}
                                size="small"
                                variant="outlined"
                                className={className + '__header__username'}
                            />
                        </div>
                        <div className={className + '__header__meta'}>
                            {designation && (
                                <span className={className + '__header__designation'}>
                                    <IoBriefcaseOutline /> {designation}
                                </span>
                            )}
                            {user?.company && (
                                <span className={className + '__header__company'}>
                                    <IoBusinessOutline /> {user?.company}
                                </span>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Personal Information */}
                <Card className={className + '__section'}>
                    <div className={className + '__section__header'}>
                        <IoPersonOutline />
                        <h2>Personal Information</h2>
                    </div>
                    <div className={className + '__section__content'}>
                        <div className={className + '__form-grid'}>
                            <TextField
                                label="Full Name"
                                value={fullName || ''}
                                onChange={handleFullNameChange}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    }
                                }}
                            />
                            <TextField
                                label="Designation"
                                value={designation || ''}
                                onChange={handleDesignationChange}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    }
                                }}
                            />
                            <TextField
                                label="Company"
                                value={user?.company || ''}
                                onChange={handleCompanyChange}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    }
                                }}
                            />
                        </div>
                    </div>
                </Card>

                {/* Contact Information */}
                <Card className={className + '__section'}>
                    <div className={className + '__section__header'}>
                        <IoMailOutline />
                        <h2>Contact Information</h2>
                    </div>
                    <div className={className + '__section__content'}>
                        <div className={className + '__form-grid'}>
                            <div className={className + '__field-with-action'}>
                                <TextField
                                    label="Email Address"
                                    value={user?.email || ''}
                                    fullWidth
                                    disabled
                                    variant="outlined"
                                    error={!isValidEmail}
                                    InputProps={{
                                        sx: {
                                            borderRadius: '12px',
                                            backgroundColor: 'var(--color-muiBackground)'
                                        }
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<IoPencil />}
                                    onClick={toggleVerifyEmailUpdateForm}
                                    className={className + '__field-action'}
                                >
                                    Update
                                </Button>
                            </div>

                            {APC_ENABLE_INTEGRATIONS_WHATSAPP && (
                                <div className={className + '__field-with-action'}>
                                    <TextField
                                        label="Contact Number"
                                        value={user?.contactNumber || 'Not provided'}
                                        fullWidth
                                        disabled
                                        variant="outlined"
                                        InputProps={{
                                            sx: {
                                                borderRadius: '12px',
                                                backgroundColor: 'var(--color-muiBackground)'
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<IoPencil />}
                                        onClick={toggleEnterContactNumberForm}
                                        className={className + '__field-action'}
                                    >
                                        {user?.contactNumber ? 'Update' : 'Add'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Location */}
                <Card className={className + '__section'}>
                    <div className={className + '__section__header'}>
                        <IoLocationOutline />
                        <h2>Location</h2>
                    </div>
                    <div className={className + '__section__content'}>
                        <div className={className + '__form-grid'}>
                            <Autocomplete
                                options={countries}
                                value={userCountry || null}
                                getOptionLabel={(option) => option?.name || ''}
                                onChange={handleCountryChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Country"
                                        variant="outlined"
                                        InputProps={{
                                            sx: {
                                                borderRadius: '12px',
                                                backgroundColor: 'var(--color-muiBackground)'
                                            }
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                options={states || []}
                                value={userState || null}
                                getOptionLabel={(option) => option || ''}
                                onChange={handleStateChange}
                                disabled={!countryCode}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="State/Province"
                                        variant="outlined"
                                        InputProps={{
                                            sx: {
                                                borderRadius: '12px',
                                                backgroundColor: countryCode ? 'var(--color-muiBackground)' : 'var(--color-muiBackground)'
                                            }
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </Card>

                {/* About */}
                <Card className={className + '__section'}>
                    <div className={className + '__section__header'}>
                        <IoInformationCircleOutline />
                        <h2>About</h2>
                    </div>
                    <div className={className + '__section__content'}>
                        <TextField
                            label="Tell us about yourself"
                            value={user?.about || ''}
                            onChange={handleAboutChange}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            placeholder="Write a short bio..."
                            InputProps={{
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: 'var(--color-muiBackground)',
                                    color: 'var(--color-text)'
                                }
                            }}
                        />
                    </div>
                </Card>

                {/* Save Bar */}
                {edited && (
                    <div className={className + '__save-bar'}>
                        <div className={className + '__save-bar__content'}>
                            <IoCheckmarkCircle className={className + '__save-bar__icon'} />
                            <span className={className + '__save-bar__text'}>You have unsaved changes</span>
                        </div>
                        <div className={className + '__save-bar__actions'}>
                            <Button
                                variant="outlined"
                                onClick={renderData}
                                disabled={cursor === 'wait'}
                            >
                                Discard
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={cursor === 'wait'}
                                startIcon={cursor === 'wait' ? null : <IoCheckmarkCircle />}
                            >
                                {cursor === 'wait' ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {showEnterContactNumberForm && (
                <EnterContactNumberForm
                    open={showEnterContactNumberForm}
                    closePopup={toggleEnterContactNumberForm}
                    reloadData={renderData}
                />
            )}
            {openVerifyEmailForm && (
                <VerifyUpdateEmailForm
                    open={openVerifyEmailForm}
                    closePopup={toggleVerifyEmailUpdateForm}
                    user={user}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}
